import {
  PRIMARY_CONTROLS_STRIP_BACK_HIDDEN,
  isPrimaryControlsStripBackOverrideEntry,
  type PrimaryControlsStripBackAppShellPlacement,
  type PrimaryControlsStripBackRegistryEntry,
  type PrimaryControlsStripBackRegistryLabelEntry,
} from "@-/dev.improve.haniverse.contexts.primary-controls-strip.back";
import { useScopedBack } from "@-/dev.improve.haniverse.routes.nav._lib.back";
import type { Href } from "@-/com.npmjs.expo-router";
import { Platform, useWindowDimensions } from "@-/com.npmjs.react-native";
import { usePrimaryControlsStripBackRegistryCurrent } from "#use-primary-controls-strip-back-registry-current";

const isWeb = Platform.OS === "web";

/**
 * The single resolved answer to "is there a back control here, and what does it do".
 *
 * `isVisible` is the ONLY visibility predicate in the back-control system. Everything that needs
 * to know whether a chevron will appear — the host that draws it, the app shell that reserves
 * horizontal space before the menu, and the panels toolbar that chooses between the shared
 * control and its in-card fallback — reads it from here.
 */
export type PrimaryControlsStripBackResolution = {
  /** `undefined` for the bare chevron (no publisher); otherwise the published label node. */
  fallbackHref: Href | undefined;
  isVisible: boolean;
  /** True when the winning entry renders a label beside the chevron, so it needs a wide lane. */
  isWide: boolean;
  label: PrimaryControlsStripBackRegistryLabelEntry["label"] | undefined;
};

const HIDDEN: PrimaryControlsStripBackResolution = {
  fallbackHref: undefined,
  isVisible: false,
  isWide: false,
  label: undefined,
};

function isLabelEntry(
  entry: PrimaryControlsStripBackRegistryEntry | null,
): entry is PrimaryControlsStripBackRegistryLabelEntry {
  return (
    entry !== null &&
    entry !== PRIMARY_CONTROLS_STRIP_BACK_HIDDEN &&
    !isPrimaryControlsStripBackOverrideEntry(entry)
  );
}

/**
 * Subscribes to the back registry and resolves the winning entry against this callsite's
 * placement.
 *
 * `appShellPlacement` names the lane asking the question. Within the native app shell the control
 * lives at the shell's leading edge in portrait; landscape can place it inside a screen's
 * actions toolbar. Exactly one lane may claim it. Passing `undefined` asks the
 * placement-agnostic question used by the default (non-app-shell) header.
 *
 * Always resolves to hidden on web: web mounts no `PrimaryControlsStripBackProvider`, so the context resolves
 * to the `defaultError` proxy and even reading `getCurrent()` would throw. Hooks above this point
 * still run unconditionally, keeping hook order stable.
 */
export function usePrimaryControlsStripBackResolution({
  appShellPlacement,
}: {
  appShellPlacement?: PrimaryControlsStripBackAppShellPlacement;
} = {}): PrimaryControlsStripBackResolution {
  const { height, width } = useWindowDimensions();
  const { canGoBack } = useScopedBack();
  const current = usePrimaryControlsStripBackRegistryCurrent();

  if (isWeb) {
    return HIDDEN;
  }

  // A screen suppressed the control entirely — no label, and no bare-chevron fallback either — or
  // filled the top-left slot with its own content (`PrimaryControlsStripLeading`).
  if (
    current === PRIMARY_CONTROLS_STRIP_BACK_HIDDEN ||
    isPrimaryControlsStripBackOverrideEntry(current)
  ) {
    return HIDDEN;
  }

  if (isLabelEntry(current)) {
    // A published entry always has a working destination (`fallbackHref` is required), so it
    // renders whether or not there is in-tab history to pop.
    // Portrait shares one header row: back always precedes the drawer button. Landscape
    // keeps screen toolbar placement because the drawer lives in a separate navigation rail.
    const placement =
      width > height ? (current.appShellPlacement ?? "account-drawer") : "account-drawer";
    const claimsThisLane = appShellPlacement === undefined || placement === appShellPlacement;
    return claimsThisLane
      ? {
          fallbackHref: current.fallbackHref,
          isVisible: true,
          isWide: true,
          label: current.label,
        }
      : HIDDEN;
  }

  // No publisher: fall back to a bare chevron, but only when an in-tab pop is actually possible —
  // without a `fallbackHref` there is nowhere else for a press to go. The account-drawer lane owns
  // this fallback; a screen that wants it in its own toolbar publishes an entry for that lane.
  const claimsThisLane = appShellPlacement === undefined || appShellPlacement === "account-drawer";
  return claimsThisLane && canGoBack
    ? { fallbackHref: undefined, isVisible: true, isWide: false, label: undefined }
    : HIDDEN;
}
